// G/H/I/J. Vendors and subprocessors, IP ownership, investor readiness and
// the regulatory calendar.
//
// Everything on this screen is a tracker. The app records whether a legal
// document exists; it is never itself the legal act, and each panel says so
// where that matters most.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import {
  CAP, can, fmtDate, humanise, legalError, plGet, plPatch, plPost,
} from "../../lib/platformLegal";
import {
  Button, Empty, ExternalActionNote, Field, Loading, Modal, Note, Section, Stat, Table,
  TonePill, inputClass,
} from "./ui";

const SUBTABS = [
  { key: "vendors", label: "Vendors & subprocessors", cap: CAP.vendors },
  { key: "ip", label: "IP ownership", cap: CAP.corporate },
  { key: "investor", label: "Investor readiness", cap: CAP.corporate },
  { key: "calendar", label: "Regulatory calendar", cap: CAP.view },
];

export default function RegistersSection({ capabilities }) {
  const available = SUBTABS.filter((t) => can(capabilities, t.cap));
  const [tab, setTab] = useState(available[0]?.key || "calendar");

  if (available.length === 0) {
    return <Empty>You do not have access to these registers.</Empty>;
  }
  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <div className="flex gap-1.5 min-w-max" role="tablist" data-testid="pl-registers-tabs">
          {available.map((item) => (
            <button key={item.key} role="tab" aria-selected={tab === item.key}
                    onClick={() => setTab(item.key)}
                    data-testid={`pl-registers-tab-${item.key}`}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium whitespace-nowrap ${
                      tab === item.key ? "border-slate-900 bg-slate-900 text-white"
                                       : "border-slate-200 bg-white text-slate-700"}`}>
              {item.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "vendors" && <Vendors capabilities={capabilities} />}
      {tab === "ip" && <IpRegister capabilities={capabilities} />}
      {tab === "investor" && <Investor capabilities={capabilities} />}
      {tab === "calendar" && <Calendar capabilities={capabilities} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Vendors({ capabilities }) {
  const [data, setData] = useState(null);
  const [annex, setAnnex] = useState(null);
  const [editing, setEditing] = useState(null);
  const writable = can(capabilities, CAP.vendors);

  const load = useCallback(async () => {
    try { setData(await plGet("/vendors")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the vendor register..." />;

  return (
    <>
      <Section testId="pl-vendor-summary" title="Vendors and subprocessors"
               description="Every third party that can see ConfirmPH data, what it sees, and
                            whether there is actually an agreement covering it."
               actions={
                 <Button variant="secondary"
                         onClick={async () => {
                           try { setAnnex(await plGet("/vendors/subprocessor-annex")); }
                           catch (err) { toast.error(legalError(err)); }
                         }}
                         data-testid="pl-annex">View subprocessor annex</Button>
               }>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Stat label="Registered" value={data.summary.total} />
          <Stat label="Approved" value={data.summary.approved} />
          <Stat label="No agreement" value={data.summary.missing_agreement.length}
                tone={data.summary.missing_agreement.length ? "text-amber-700" : "text-slate-900"} />
          <Stat label="Handle sensitive data" value={data.summary.sensitive_vendors} />
        </div>
        {data.summary.missing_agreement.length > 0 && (
          <Note tone="amber" testId="pl-vendor-gap">
            A missing agreement is an outstanding action, never an assumption that one exists:{" "}
            {data.summary.missing_agreement.join(", ")}.
          </Note>
        )}
      </Section>

      <Section testId="pl-vendors">
        <Table head={["Vendor", "Purpose", "Agreement", "Risk", "Status", ""]}
               testId="pl-vendor-table" minWidth={760}>
          {data.vendors.map((row) => (
            <tr key={row.key} data-testid={`pl-vendor-${row.key}`}>
              <td className="py-2 pr-3">
                <div className="font-medium text-slate-900">{row.vendor}</div>
                <div className="text-xs text-slate-500">{humanise(row.category)}</div>
              </td>
              <td className="py-2 pr-3 text-xs text-slate-700">{row.purpose}</td>
              <td className="py-2 pr-3 text-xs">{humanise(row.agreement)}</td>
              <td className="py-2 pr-3 text-xs">{humanise(row.risk)}</td>
              <td className="py-2 pr-3">
                <TonePill tone={row.status === "approved"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-900"}>
                  {humanise(row.status)}
                </TonePill>
              </td>
              <td className="py-2 pr-3">
                {writable && (
                  <Button variant="secondary" onClick={() => setEditing(row)}
                          data-testid={`pl-vendor-edit-${row.key}`}>Edit</Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </Section>

      {annex && (
        <Modal title="Subprocessor annex" onClose={() => setAnnex(null)} testId="pl-annex-modal"
               description="Generated from the vendor register, for the Data Processing Addendum.">
          <Table head={["Vendor", "Data", "Agreement in place"]} testId="pl-annex-table">
            {annex.subprocessors.map((row) => (
              <tr key={row.vendor}>
                <td className="py-2 pr-3 text-xs">{row.vendor}</td>
                <td className="py-2 pr-3 text-xs">{(row.data_accessed || []).join(", ")}</td>
                <td className="py-2 pr-3 text-xs">{row.agreement_in_place ? "Yes" : "No"}</td>
              </tr>
            ))}
          </Table>
          <Note tone="amber">{annex.note}</Note>
        </Modal>
      )}

      {editing && (
        <VendorModal vendor={editing} options={data} onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
    </>
  );
}

function VendorModal({ vendor, options, onClose, onSaved }) {
  const [form, setForm] = useState({
    status: vendor.status, risk: vendor.risk, agreement: vendor.agreement,
    agreement_reference: vendor.agreement_reference || "", owner: vendor.owner || "",
    renewal_at: vendor.renewal_at || "", reason: "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await plPatch(`/vendors/${vendor.key}`, form);
      toast.success("Vendor updated.");
      onSaved();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={vendor.vendor} onClose={onClose} testId="pl-vendor-modal">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Status">
          <select className={inputClass} value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {options.statuses.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
          </select>
        </Field>
        <Field label="Risk">
          <select className={inputClass} value={form.risk}
                  onChange={(e) => setForm({ ...form, risk: e.target.value })}>
            {options.risks.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
          </select>
        </Field>
        <Field label="Agreement">
          <select className={inputClass} value={form.agreement}
                  onChange={(e) => setForm({ ...form, agreement: e.target.value })}>
            {options.agreements.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
          </select>
        </Field>
        <Field label="Next review">
          <input type="date" className={inputClass} value={form.renewal_at}
                 onChange={(e) => setForm({ ...form, renewal_at: e.target.value })} />
        </Field>
      </div>
      <Field label="Agreement reference">
        <input className={inputClass} value={form.agreement_reference}
               onChange={(e) => setForm({ ...form, agreement_reference: e.target.value })} />
      </Field>
      <Field label="Owner">
        <input className={inputClass} value={form.owner}
               onChange={(e) => setForm({ ...form, owner: e.target.value })} />
      </Field>
      <Field label="Reason" hint="Required when approving a vendor.">
        <input className={inputClass} value={form.reason}
               onChange={(e) => setForm({ ...form, reason: e.target.value })} />
      </Field>
      <Note tone="slate">
        A vendor cannot be approved with no agreement recorded. If only the provider's standard
        terms apply, record that explicitly rather than leaving it blank.
      </Note>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={save} busy={busy} data-testid="pl-vendor-save">Save</Button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function IpRegister({ capabilities }) {
  const [data, setData] = useState(null);
  const writable = can(capabilities, CAP.corporate);

  const load = useCallback(async () => {
    try { setData(await plGet("/ip")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the IP register..." />;

  const update = async (key) => {
    const owner = window.prompt("Who owns this asset now?");
    if (!owner) return;
    const reason = window.prompt("Which assignment document, and when?");
    if (!reason) return;
    try {
      await plPatch(`/ip/${key}`, { status: "assigned", current_owner: owner, reason });
      toast.success("Assignment recorded.");
      load();
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  return (
    <Section testId="pl-ip" title="Intellectual property and assets"
             description={data.summary.note}>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Stat label="Registered" value={data.summary.total} />
        <Stat label="Assignment required" value={data.summary.assignment_required} />
        <Stat label="Assigned" value={data.summary.assigned}
              tone={data.summary.founder_ip_assignment_complete
                ? "text-emerald-700" : "text-amber-700"} />
      </div>
      <Table head={["Asset", "Status", "Current owner", "Intended owner", ""]}
             testId="pl-ip-table" minWidth={680}>
        {data.assets.map((row) => (
          <tr key={row.key} data-testid={`pl-ip-${row.key}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{row.asset}</div>
              <div className="text-xs text-slate-500">{row.note}</div>
            </td>
            <td className="py-2 pr-3">
              <TonePill tone={row.status === "assigned"
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-amber-200 bg-amber-50 text-amber-900"}>
                {humanise(row.status)}
              </TonePill>
            </td>
            <td className="py-2 pr-3 text-xs">{row.current_owner || "—"}</td>
            <td className="py-2 pr-3 text-xs">{row.intended_owner || "—"}</td>
            <td className="py-2 pr-3">
              {writable && row.assignment_required && row.status !== "assigned" && (
                <Button variant="secondary" onClick={() => update(row.key)}
                        data-testid={`pl-ip-assign-${row.key}`}>Record assignment</Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
      <ExternalActionNote>
        Executing an assignment is a legal act performed outside ConfirmPH. Nothing is marked as
        owned by an entity before a real transfer has happened and the deed is attached.
      </ExternalActionNote>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Investor({ capabilities }) {
  const [data, setData] = useState(null);
  const writable = can(capabilities, CAP.corporate);

  const load = useCallback(async () => {
    try { setData(await plGet("/investor")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading investor readiness..." />;

  const foreign = data.foreign_equity || {};
  return (
    <>
      <Section testId="pl-investor" title="Investor readiness"
               description={data.capabilities_note}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Stat label="Items" value={data.summary.total} />
          <Stat label="Complete" value={data.summary.complete} />
          <Stat label="Blocked" value={data.summary.blocked}
                tone={data.summary.blocked ? "text-rose-700" : "text-slate-900"} />
        </div>
        <Table head={["Item", "Status", "Note"]} testId="pl-investor-table" minWidth={560}>
          {data.items.map((row) => (
            <tr key={row.key} data-testid={`pl-investor-${row.key}`}>
              <td className="py-2 pr-3 font-medium text-slate-900">{row.label}</td>
              <td className="py-2 pr-3">
                <TonePill tone={["complete", "not_applicable"].includes(row.status)
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : "border-amber-200 bg-amber-50 text-amber-900"}>
                  {humanise(row.status)}
                </TonePill>
              </td>
              <td className="py-2 pr-3 text-xs text-slate-600">{row.note || "—"}</td>
            </tr>
          ))}
        </Table>
        <ExternalActionNote>
          ConfirmPH cannot issue shares, record a transfer of ownership, or accept investment.
        </ExternalActionNote>
      </Section>

      <Section testId="pl-foreign-equity" title="Foreign equity review">
        <div className="flex flex-wrap items-center gap-2">
          <TonePill tone={foreign.status === "complete"
            ? "border-emerald-200 bg-emerald-50 text-emerald-800"
            : foreign.status === "review_required"
              ? "border-rose-200 bg-rose-50 text-rose-700"
              : "border-slate-200 bg-slate-50 text-slate-600"}
            testId="pl-foreign-status">
            {humanise(foreign.status || "not_required")}
          </TonePill>
          {data.investment_blocked && (
            <span className="text-xs text-rose-700 font-medium">Investment blocked</span>
          )}
        </div>
        <Note tone="slate" testId="pl-foreign-note">
          ConfirmPH never computes how much foreign ownership is permitted. That is a question for
          counsel, and the answer changes; the app records only whether the review happened.
        </Note>
        {writable && (
          <div className="mt-3">
            <Button variant="secondary" onClick={async () => {
              const percent = window.prompt("Proposed foreign ownership percentage (0 for none):");
              if (percent === null) return;
              try {
                await plPost("/investor/foreign-equity", {
                  foreign_investor: Number(percent) > 0, proposed_percent: Number(percent) });
                toast.success("Recorded.");
                load();
              } catch (err) {
                toast.error(legalError(err));
              }
            }} data-testid="pl-foreign-declare">Declare proposed foreign ownership</Button>
          </div>
        )}
      </Section>

      <Section testId="pl-cap-table" title="Cap table mirror">
        <Note tone="amber" testId="pl-cap-disclaimer">{data.cap_table.disclaimer}</Note>
        {data.cap_table.entries?.length > 0 ? (
          <Table head={["Holder", "Class", "Percent"]} testId="pl-cap-table-table">
            {data.cap_table.entries.map((row) => (
              <tr key={row.holder}>
                <td className="py-2 pr-3 text-sm">{row.holder}</td>
                <td className="py-2 pr-3 text-xs">{row.class || "—"}</td>
                <td className="py-2 pr-3 text-xs tabular-nums">{row.percent}%</td>
              </tr>
            ))}
          </Table>
        ) : (
          <Empty testId="pl-cap-empty">No informational cap table recorded.</Empty>
        )}
      </Section>
    </>
  );
}

// ---------------------------------------------------------------------------
function Calendar({ capabilities }) {
  const [data, setData] = useState(null);
  const writable = can(capabilities, CAP.calendar);

  const load = useCallback(async () => {
    try { setData(await plGet("/calendar")); } catch (err) { toast.error(legalError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading label="Reading the compliance calendar..." />;

  const schedule = async (key) => {
    const due = window.prompt("Due date (YYYY-MM-DD):");
    if (!due) return;
    try {
      await plPatch(`/calendar/${key}`, { due_date: due,
                                          rule_last_verified_at: new Date().toISOString().slice(0, 10) });
      toast.success("Scheduled.");
      load();
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  return (
    <Section testId="pl-calendar" title="Regulatory and compliance calendar"
             description={data.summary.note}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Entries" value={data.summary.total} />
        <Stat label="Scheduled" value={data.summary.scheduled} />
        <Stat label="Overdue" value={data.summary.overdue}
              tone={data.summary.overdue ? "text-rose-700" : "text-slate-900"} />
        <Stat label="Needs verification" value={data.summary.needs_verification}
              tone={data.summary.needs_verification ? "text-amber-700" : "text-slate-900"} />
      </div>
      <Table head={["Event", "Authority", "Due", "Status", ""]} testId="pl-calendar-table"
             minWidth={680}>
        {data.events.map((row) => (
          <tr key={row.key} data-testid={`pl-calendar-${row.key}`}>
            <td className="py-2 pr-3">
              <div className="font-medium text-slate-900">{row.title}</div>
              <div className="text-xs text-slate-500">{row.requirement_note}</div>
              {row.needs_current_verification && (
                <div className="mt-1 text-[11px] text-amber-800">
                  Needs current verification — confirm the rule before relying on it.
                </div>
              )}
            </td>
            <td className="py-2 pr-3 text-xs">{row.authority.toUpperCase()}</td>
            <td className="py-2 pr-3 text-xs">
              {fmtDate(row.due_date)}
              {row.due_state === "overdue" && (
                <div className="text-rose-700">Overdue</div>
              )}
            </td>
            <td className="py-2 pr-3 text-xs">{humanise(row.status)}</td>
            <td className="py-2 pr-3">
              {writable && (
                <Button variant="secondary" onClick={() => schedule(row.key)}
                        data-testid={`pl-calendar-schedule-${row.key}`}>Schedule</Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
    </Section>
  );
}
